import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Target, Zap, Bot, ArrowLeft, Flag, Volume2, VolumeX, Car as CarIcon, Eye, Coins, Maximize2, Minimize2 } from "lucide-react";

import GameCanvas from "@/components/GameCanvas";
import QuizModal from "@/components/QuizModal";
import RiddleModal from "@/components/RiddleModal";
import BossFightModal from "@/components/BossFightModal";
import { useAuth } from "@/context/AuthContext";
import sfx from "@/lib/sfx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function wsUrlFor(lobbyId, token, name) {
  // Same logic as axios interceptor in App.js — use the current origin when
  // deployed on a custom domain so the WebSocket connects through the same
  // ingress that routes /api/* to the backend.
  const httpBase =
    typeof window !== "undefined" && window.location.origin !== BACKEND_URL
      ? window.location.origin
      : BACKEND_URL;
  const base = httpBase.replace(/^http/, "ws");
  return `${base}/api/ws/race/${lobbyId}?token=${encodeURIComponent(token)}&name=${encodeURIComponent(name)}`;
}

function getCookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

export default function Race() {
  const { lobbyId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [city, setCity] = useState(null);
  const [car, setCar] = useState(null);
  // Trunk-Monkey cinematic overlay: start timestamp when E is pressed.
  // The overlay auto-dismisses after ~1500ms.
  const [monkeyCinematicAt, setMonkeyCinematicAt] = useState(0);
  const [hp, setHp] = useState(100);
  const [weapons, setWeapons] = useState({ missile: 0, rocket_boost: 0, monkey_bomb: 0 });
  const [collected, setCollected] = useState(0);
  const [catchphrase, setCatchphrase] = useState(null);  // user's saved phrase
  const [ultimateState, setUltimateState] = useState({ ready: false, used: false, label: "" });
  // Fire a one-shot toast + sound when the ultimate first becomes ready.
  useEffect(() => {
    if (ultimateState.ready) {
      sfx.ultimateReady?.();
      toast.success(`⚡ ${ultimateState.label} ready — press U or Enter`, {
        duration: 4500,
        id: "ultimate-ready", // dedupe so it can't fire twice in a row
      });
    }
  }, [ultimateState.ready, ultimateState.label]);
  const [elapsed, setElapsed] = useState(0);
  const [remote, setRemote] = useState({});
  const [quiz, setQuiz] = useState({ open: false, item: null });
  const [raceEnded, setRaceEnded] = useState(null);
  const [muted, setMuted] = useState(false);
  const [viewMode, setViewMode] = useState("map");  // map | cockpit
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Mirror the browser fullscreen state so the icon flips when the user
  // hits Esc or F11 instead of clicking our button.
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (_e) { /* user cancelled or browser blocked */ }
  }, []);
  // F key shortcut — but ignore when the user is typing in an input/textarea
  // (e.g., the catchphrase setter, sidekick name field).
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "f" && e.key !== "F") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.target?.isContentEditable) return;
      e.preventDefault();
      toggleFullscreen();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleFullscreen]);
  const [feed, setFeed] = useState([]);
  const [coinFlashes, setCoinFlashes] = useState([]);  // floating -50 texts
  const [currency, setCurrency] = useState(user?.currency ?? 0);
  const [pedestriansHit, setPedestriansHit] = useState(0);
  const [quizCache, setQuizCache] = useState({}); // item_id → quiz, pre-fetched
  // Tourism Bonus: live progress through the city's signature props.
  const [tourism, setTourism] = useState({ visited: 0, total: 0 });
  // Jail state — driven by GameCanvas via onJailChange. Used to render the
  // Sell-Artifact escape button when the player is in lockup.
  const [jail, setJail] = useState({ active: false });
  // Sidekick state — designed in the Garage. If the player has a credit
  // saved (earned in-race by the sidekick-trigger), it's silently consumed
  // at race start and the buddy spawns. No popup, no timer (per user
  // request: "make sure it never pops up in games ever again").
  const [sidekickDesign, setSidekickDesign] = useState(null);

  // Boxing boss fight — trigger when the player wins the race (collects
  // all artifacts). The boss fight runs in a modal AFTER the WS finish
  // event; if the player wins it, they earn the city completion badge
  // + currency bonus + 2 newly unlocked cities. Loss = race summary
  // shown without the boss prize, but the race itself still counts.
  const [bossFight, setBossFight] = useState(null);     // { city_id }
  const [bossDriver, setBossDriver] = useState(null);   // driver appearance
  const [bossDefeated, setBossDefeated] = useState(false);

  // Load driver appearance once so it's ready when the boss fight kicks in.
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/driver`, { withCredentials: true })
      .then((r) => setBossDriver(r.data.driver))
      .catch(() => setBossDriver(null));
  }, []);

  const wsRef = useRef(null);
  const pickupResolverRef = useRef(null);
  const remoteHandlersRef = useRef({});
  const canvasActionsRef = useRef({}); // { onCorrectQuiz, sellItemToEscape, applyCopSteal, copStealRejected }
  const startRef = useRef(performance.now());

  // Cop-car steal availability — fetched at race load so the canvas can
  // gate the S+Shift trigger without an extra round-trip per frame.
  const [copStealStatus, setCopStealStatus] = useState({ available: false, races_until_next: 0 });
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/cop_steal/status`, { withCredentials: true })
      .then((r) => setCopStealStatus(r.data || { available: false }))
      .catch(() => setCopStealStatus({ available: false }));
  }, []);
  const onCopStealTrigger = useCallback(async () => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/cop_steal/use`, {}, { withCredentials: true });
      if (r.status === 200) {
        canvasActionsRef.current?.applyCopSteal?.();
        setCopStealStatus({ available: false, races_until_next: 3 });
        toast.success("🚓 STOLE THE COP CAR — INVINCIBLE!", { duration: 4500 });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Cop steal not available";
      toast.error(detail);
      canvasActionsRef.current?.copStealRejected?.();
    }
  }, []);
  // Item IDs the player just submitted an answer for. Prevents the Dialog's
  // auto-close cascade from firing the pickup resolver with `false` and
  // causing the quiz to re-open in a loop before the WS `pickup_result`
  // arrives.
  const answeredRef = useRef(new Set());

  // Load city + car
  useEffect(() => {
    (async () => {
      try {
        const l = await axios.get(`${BACKEND_URL}/api/lobbies/${lobbyId}`);
        const c = await axios.get(`${BACKEND_URL}/api/cities/${l.data.city_id}`);
        const g = await axios.get(`${BACKEND_URL}/api/garage`, { withCredentials: true });

        // If this lobby was created from a custom course, swap the city's
        // items for the course items (each carries its own AI-generated
        // quiz baked in). Keep the city's theme + obstacles + scenery so
        // the world still feels like the player's chosen city.
        let cityData = c.data;
        let courseQuizMap = null;
        let courseIdForWS = null;
        if (l.data.course_id) {
          try {
            const cr = await axios.get(
              `${BACKEND_URL}/api/courses/${l.data.course_id}`,
              { withCredentials: true },
            );
            const courseItems = (cr.data.items || []).map((it) => ({
              // Map to the same shape city.items uses elsewhere on the
              // canvas + HUD (id, name, subject, x, y, icon_url).
              id: it.item_id,
              name: it.name,
              subject: it.name,
              x: it.x,
              y: it.y,
              icon_url: it.icon_url || null,
            }));
            cityData = { ...c.data, items: courseItems };
            courseQuizMap = {};
            for (const it of cr.data.items || []) {
              courseQuizMap[it.item_id] = it.quiz;
            }
            courseIdForWS = cr.data.course_id;
          } catch (_e) {
            // Fall back to vanilla city items if course fetch fails.
          }
        }

        setCity({ ...cityData, course_id: courseIdForWS });
        // Attach the trail cosmetic (if active) onto the car object so
        // GameCanvas can render a colored exhaust behind the player.
        const trailId = g.data.active_trail;
        const trailCos = trailId ? (g.data.cosmetics || {})[trailId] : null;
        setCar({
          ...g.data.car,
          active_trail_id: trailId || null,
          active_trail_color: trailCos?.color || null,
        });
        setWeapons(g.data.car.weapons || { missile: 0, rocket_boost: 0, monkey_bomb: 0 });
        setHp(g.data.car.hp);
        setCurrency(g.data.user.currency);
        // Initialize the Tourism HUD with the city's signature-prop target so
        // "Tourist 0/Y" renders the moment the race loads (not only after the
        // first prop-touch). Course races skip the tourism mechanic — set 0.
        setTourism({
          visited: 0,
          total: courseIdForWS ? 0 : (cityData.tourism?.prop_count ?? 0),
        });

        // For vanilla races, pre-fetch all quizzes; for course races we
        // already have them inline (no LLM round-trip needed).
        if (courseQuizMap) {
          setQuizCache(courseQuizMap);
        } else {
          const results = await Promise.all(
            cityData.items.map((it) =>
              axios.get(`${BACKEND_URL}/api/quiz/${cityData.id}/${it.id}`).then((r) => [it.id, r.data]).catch(() => [it.id, null])
            )
          );
          const map = {};
          for (const [id, q] of results) if (q) map[id] = q;
          setQuizCache(map);
        }

        // Auto-deploy buddy if the player has at least one credit AND
        // a design saved (designed in the Garage). The credit is
        // silently consumed here — no popup, no timer, no in-race UI.
        // This was iterated to address the user's complaint: "make sure
        // it never pops up in games ever again".
        try {
          const skMe = await axios.get(`${BACKEND_URL}/api/sidekick/me`, {
            withCredentials: true,
          });
          if ((skMe.data?.credits || 0) >= 1 && skMe.data?.design) {
            const consume = await axios.post(
              `${BACKEND_URL}/api/sidekick/consume`,
              {},
              { withCredentials: true },
            );
            if (consume.data?.used) {
              setSidekickDesign(consume.data.design || skMe.data.design);
            }
          }
        } catch (_e) { /* non-fatal — race continues without buddy */ }
        // Load the user's catchphrase (if approved) — popped over the car
        // when they grab an artifact during the race.
        try {
          const cp = await axios.get(`${BACKEND_URL}/api/profile/catchphrase/me`, {
            withCredentials: true,
          });
          if (cp.data?.catchphrase) setCatchphrase(cp.data.catchphrase);
        } catch (_e) { /* silent */ }
      } catch (e) {
        toast.error("Could not load race");
        navigate("/lobbies");
      }
    })();
  }, [lobbyId, navigate]);

  useEffect(() => { sfx.init(); }, []);

  // WebSocket
  useEffect(() => {
    if (!user || !car) return;
    const token = getCookie("session_token");
    if (!token) return;
    const ws = new WebSocket(wsUrlFor(lobbyId, token, user.name));
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch (e) { return; }
      const me = user.user_id;

      switch (msg.type) {
        case "pos":
          if (msg.user_id === me) return;
          setRemote((prev) => ({
            ...prev,
            [msg.user_id]: {
              x: msg.x, y: msg.y, angle: msg.angle, name: msg.name,
              body_color: msg.body_color, accent_color: msg.accent_color,
              kind: msg.kind || "cruiser",
            },
          }));
          break;
        case "missile_fired":
          remoteHandlersRef.current.onRemoteMissile?.(msg);
          break;
        case "monkey_deployed":
          remoteHandlersRef.current.onRemoteMonkey?.(msg);
          break;
        case "player_damaged":
          remoteHandlersRef.current.onPlayerDamaged?.(msg);
          if (msg.user_id === me) setHp(msg.hp);
          break;
        case "coin_penalty":
          // We hit a pedestrian — server deducted coins
          setCurrency(msg.currency);
          setPedestriansHit(msg.pedestrians_hit ?? (pedestriansHit + 1));
          // floating "-50¢" animation
          setCoinFlashes((f) => [
            ...f,
            { id: Math.random().toString(36).slice(2), amount: msg.amount, ts: Date.now() },
          ]);
          break;
        case "pedestrian_hit":
          if (msg.user_id !== me) {
            setFeed((f) => [
              { id: `ped-${Date.now()}`, text: `${msg.name} ran over a pedestrian. Oof.` },
              ...f,
            ].slice(0, 4));
          }
          break;
        case "pickup_result":
          // Whatever the outcome, the client-side "this item was answered"
          // flag can be cleared — the server has responded.
          if (msg.item_id) answeredRef.current.delete(msg.item_id);
          // The client has already optimistically resolved the pickup using
          // the cached quiz answer (see onQuizAnswer). The WS reply is now
          // informational — except in the rare case where we did NOT have a
          // cached quiz (so we deferred to the server). In that case, fire
          // the resolver here.
          // We can detect the "deferred" case because the resolver will be
          // a no-op for already-collected items (it.collected===true skips
          // the mutation), so calling it again is safe.
          if (msg.correct && msg.ok) {
            pickupResolverRef.current?.(msg.item_id, true);
            if (msg.finished) toast.success("You collected all items!");
          } else if (msg.correct && !msg.ok && msg.reason && msg.reason !== "already_collected") {
            // Server rejected the pickup for a reason other than dup
            // (e.g., race_expired). Show feedback but don't override the
            // optimistic state — the player already saw the artifact go.
            toast.error(`Pickup rejected: ${msg.reason}`);
          }
          // No-op for { correct:false } — client already decided locally
          // and the resolver was called with false there too.
          break;
        case "item_collected":
          if (msg.by_user_id !== me) {
            setFeed((f) => [
              { id: `${msg.item_id}-${Date.now()}`, text: `${msg.by_name} grabbed an item (${msg.items_collected}/${city?.items?.length ?? 5})` },
              ...f,
            ].slice(0, 4));
            if (msg.finished) toast(`${msg.by_name} completed the hunt!`, { icon: "🏁" });
          }
          break;
        case "player_joined":
          if (msg.user_id !== me) toast(`${msg.name} joined the race`);
          break;
        case "player_left":
          setRemote((prev) => {
            const { [msg.user_id]: _, ...rest } = prev;
            return rest;
          });
          break;
        default:
          break;
      }
    };
    return () => ws.close();
    // eslint-disable-next-line
  }, [user, car, lobbyId]);

  const sendEvent = useCallback((event) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(event));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setElapsed((performance.now() - startRef.current) / 1000), 200);
    return () => clearInterval(t);
  }, []);

  // clean old floating coin flashes
  useEffect(() => {
    if (!coinFlashes.length) return;
    const t = setInterval(() => {
      setCoinFlashes((list) => list.filter((f) => Date.now() - f.ts < 1400));
    }, 400);
    return () => clearInterval(t);
  }, [coinFlashes.length]);

  const onItemTouched = useCallback((item) => setQuiz({ open: true, item }), []);
  // Two-artifacts-in-10-seconds tracker → grants a sidekick credit.
  const recentPickupsRef = useRef([]);  // array of timestamps, ms
  const sidekickEarnedRef = useRef(false);  // gate: 1 grant per race max
  const onQuizAnswer = useCallback((answerIndex) => {
    const item = quiz.item;
    if (!item) return;
    // Flag this item as "answered" so the Dialog close cascade can't
    // prematurely unlock pickup with a (false) resolver call.
    answeredRef.current.add(item.id);

    // CLIENT-FIRST RESOLUTION: We already have the authoritative quiz
    // (correct_index) cached locally — same source the server uses. Resolve
    // immediately so the game doesn't get stuck if the WebSocket dropped.
    // The server-side broadcast still happens via `sendEvent` for
    // multiplayer item_collected sync + race_store tally.
    const cached = quizCache[item.id];
    const correct = cached ? Number(answerIndex) === Number(cached.correct_index) : null;
    if (correct === true) {
      pickupResolverRef.current?.(item.id, true);
      // Notify GameCanvas — bumps correctQuizCount; A-Team van rescue spawns
      // automatically when count reaches 2.
      canvasActionsRef.current?.onCorrectQuiz?.();
      if (collected + 1 === 2) {
        toast.success("🚐 The A-Team is on the way!", { duration: 4000 });
      }
      // ── Sidekick credit: 2 correct answers within 10 seconds → grant.
      const now = performance.now();
      const recent = recentPickupsRef.current.filter((t) => now - t < 10_000);
      recent.push(now);
      recentPickupsRef.current = recent;
      if (recent.length >= 2 && !sidekickEarnedRef.current) {
        sidekickEarnedRef.current = true;
        axios
          .post(`${BACKEND_URL}/api/sidekick/grant`, {}, { withCredentials: true })
          .then((r) => {
            if (r.data?.newly_added) {
              toast.success("⭐ SIDEKICK UNLOCKED — design your wingman before the next race!", { duration: 6000 });
            }
          })
          .catch(() => null);
      }
    } else if (correct === false) {
      pickupResolverRef.current?.(item.id, false);
    }
    // If we don't have a cached quiz (rare — pre-fetch failed), fall back to
    // server-authoritative WS round-trip handled in the `pickup_result` case.

    sendEvent({
      type: "pickup_attempt",
      city_id: city.id,
      course_id: city.course_id || null,
      item_id: item.id,
      answer_index: answerIndex,
    });
    setQuiz({ open: false, item: null });
  }, [quiz, sendEvent, city, quizCache]);
  const onQuizClose = useCallback(() => {
    const itemId = quiz.item?.id;
    setQuiz({ open: false, item: null });
    if (!itemId) return;
    if (answeredRef.current.has(itemId)) return;
    // User dismissed without answering (X / ESC / outside click) — unlock so
    // they can re-collide with the artifact later.
    pickupResolverRef.current?.(itemId, false);
  }, [quiz]);

  const onPedestrianHit = useCallback(() => {
    sendEvent({ type: "hit_pedestrian" });
  }, [sendEvent]);

  const onFinish = useCallback(
    async ({ items_collected, time_seconds, finished, tourism_visited, tourism_target }) => {
      if (raceEnded) return;
      setRaceEnded({ items_collected, time_seconds, finished });
      // Capture sidekick survival snapshot BEFORE posting — the canvas
      // unmounts shortly after race-end which would clear st.sidekick.
      const sk = canvasActionsRef.current?.getSidekickStatus?.() || {};
      try {
        const res = await axios.post(
          `${BACKEND_URL}/api/race/finish`,
          {
            lobby_id: lobbyId,
            items_collected,
            time_seconds,
            finished,
            tourism_visited: tourism_visited ?? 0,
            sidekick_deployed: !!sk.deployed,
            sidekick_survived: !!sk.survived,
          },
          { withCredentials: true }
        );
        toast.success(`+${res.data.reward} credits`);
        // Custom-course creator share — tells the player their run paid
        // out a small bounty to the course creator (warm fuzzies + makes
        // the creator economy visible).
        if (res.data.creator_share_paid && res.data.course_name) {
          toast(
            `🎨 +${res.data.creator_share_paid}¢ creator share → "${res.data.course_name}"`,
            { duration: 5000, icon: "🪙" },
          );
        }
        // Custom-course personal best — fires whenever this is the
        // player's fastest time on this course. Shows their rank too.
        if (res.data.course_new_pb && res.data.course_new_best) {
          const t = Number(res.data.course_new_best).toFixed(2);
          const pos = res.data.course_position ? ` · #${res.data.course_position}` : "";
          const prev = res.data.course_previous_best
            ? ` (was ${Number(res.data.course_previous_best).toFixed(2)}s)`
            : " (first finish!)";
          toast.success(`🏆 NEW PERSONAL BEST: ${t}s${pos}${prev}`, { duration: 7000 });
        }
        // Under-15s win toast — distinguish solo vs competitive.
        if (res.data.under_15s) {
          if (res.data.competitive_win) {
            toast.success("🥇 COMPETITIVE WIN — +1000 points!", { duration: 6000 });
          } else if (res.data.won) {
            toast.success("⚡ Under 15s — couple points (+50)", { duration: 4000 });
          }
        }
        // Wingman badge — sidekick survived an entire race.
        if (res.data.wingman_earned) {
          toast.success("✈️ WINGMAN BADGE UNLOCKED — your sidekick survived!", { duration: 7000 });
        }
        // Newly unlocked cities (under-15s clears unlock the next pair).
        if (Array.isArray(res.data.newly_unlocked) && res.data.newly_unlocked.length > 0) {
          const list = res.data.newly_unlocked.join(", ").toUpperCase();
          toast.success(`🗺️ NEW CITIES UNLOCKED: ${list}`, { duration: 7000 });
        }
        if (res.data.local_legend && res.data.badge) {
          toast.success(`🏆 ${res.data.badge} unlocked! +${res.data.tourism_bonus} bonus`, { duration: 6000 });
        }
        setRaceEnded({
          items_collected: res.data.items_collected,
          time_seconds: res.data.time_seconds,
          finished: res.data.won || res.data.finished,
          won: res.data.won,
          competitive_win: !!res.data.competitive_win,
          under_15s: !!res.data.under_15s,
          reward: res.data.reward,
          pedestrians_hit: res.data.pedestrians_hit ?? 0,
          penalty: res.data.penalty ?? 0,
          tourism_visited: res.data.tourism_visited ?? 0,
          tourism_target: res.data.tourism_target ?? tourism_target ?? 0,
          tourism_bonus: res.data.tourism_bonus ?? 0,
          local_legend: !!res.data.local_legend,
          badge: res.data.badge ?? null,
        });
        refresh?.();
        // BOSS FIGHT — if the player won the race (collected all artifacts),
        // trigger the boxing boss before the player exits. Defeating the
        // boss is what unlocks the next 2 cities + per-city Champion badge.
        if (res.data.won && city?.id && !bossDefeated) {
          // Brief delay so the player sees the +credits toast first.
          setTimeout(() => setBossFight({ city_id: city.id }), 1500);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [lobbyId, raceEnded, refresh, city, bossDefeated]
  );

  const onTourismProgress = useCallback(({ visited, total }) => {
    setTourism({ visited, total });
  }, []);

  // Stable refs/handlers for GameCanvas — preventing inline closures that
  // would re-trigger the canvas's main-loop useEffect on every render.
  const handleJailChange = useCallback((s) => setJail(s), []);
  const registerCanvasActions = useCallback((a) => { canvasActionsRef.current = a; }, []);
  const handleRegisterRemoteHandlers = useCallback((h) => { remoteHandlersRef.current = h; }, []);
  const handleRegisterPickupResolver = useCallback((fn) => { pickupResolverRef.current = fn; }, []);

  // Active route index for the city — driving off the right edge increments,
  // driving off the left edge decrements (modular). Each route has a fresh
  // item + obstacle layout (mirrored on the backend) so it feels like a new
  // district while preserving collected progress.
  const routeRef = useRef(0);
  // Riddle-boss state — opened when the player encounters the boss on route 1.
  const [riddle, setRiddle] = useState({ open: false, question: null, options: [], correct_index: 0, bossName: "" });
  // Synchronous mirror of riddle data so handleBossEncounter (called from
  // GameCanvas's physics loop) can open the modal without waiting for React
  // to flush setRiddle.
  const riddleDataRef = useRef(null);

  const handleRouteEdgeCross = useCallback(async (dir) => {
    if (!city) return;
    // 3 routes total: 0 = main, 1 = boss A district, 2 = boss B district.
    const next = ((routeRef.current + (dir === "right" ? 1 : -1)) % 3 + 3) % 3;
    routeRef.current = next;
    try {
      // Fetch route data + (if entering a boss route) the riddle for that
      // specific boss. Boss index = routeIndex - 1 (route 1 → boss 0,
      // route 2 → boss 1).
      const bossIndex = next >= 1 ? next - 1 : null;
      const [routeRes, riddleRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cities/${city.id}?route=${next}`, { withCredentials: true }),
        bossIndex !== null
          ? axios.get(`${BACKEND_URL}/api/riddle/${city.id}?boss_index=${bossIndex}`, { withCredentials: true }).catch(() => null)
          : Promise.resolve(null),
      ]);
      if (!routeRes?.data) {
        toast.error("Route failed to load");
        return;
      }
      const bossName = riddleRes?.data?.boss_name || (bossIndex === 1 ? "CHAMPION" : "RIDDLE BOSS");
      // IMPORTANT — set riddle state BEFORE swapping the route to avoid the
      // boss-collision-fires-before-React-commits race condition.
      if (riddleRes?.data) {
        const r = {
          question: riddleRes.data.question,
          options: riddleRes.data.options,
          correct_index: riddleRes.data.correct_index,
          bossName,
        };
        riddleDataRef.current = r;
        setRiddle({ open: false, ...r });
      } else {
        riddleDataRef.current = null;
      }
      canvasActionsRef.current?.loadRoute?.({
        items: routeRes.data.items,
        obstacles: routeRes.data.obstacles,
        fromDir: dir,
        routeIndex: next,
        riddleBossName: bossName,
        riddleVariant: bossIndex ?? 0,
      });
      const districtName = next === 0 ? "MAIN ROUTE" : next === 1 ? "DISTRICT 2" : "DISTRICT 3";
      toast(dir === "right" ? `→ ${districtName}` : `← ${districtName}`, { duration: 2200 });
    } catch (e) {
      console.error("route load failed", e);
    }
  }, [city]);

  const handleBossEncounter = useCallback(() => {
    const r = riddleDataRef.current;
    if (!r) return;
    setRiddle({ ...r, open: true });
  }, []);

  const handleRiddleAnswer = useCallback(async (idx) => {
    const r = riddleDataRef.current;
    if (!r) { setRiddle((cur) => ({ ...cur, open: false })); return; }
    if (idx === r.correct_index) {
      toast.success("🥊 Boss steps aside!");
      canvasActionsRef.current?.markRiddleSolved?.();
      setRiddle((cur) => ({ ...cur, open: false }));
      // Record the boss defeat — backend tracks toward the GOLDEN GLOBETROTTER
      // award (defeat all 20 bosses → +5000 credits + permanent badge).
      try {
        const bossIndex = (routeRef.current === 2) ? 1 : 0;
        const res = await axios.post(
          `${BACKEND_URL}/api/boss/defeat`,
          { city_id: city?.id, boss_index: bossIndex },
          { withCredentials: true }
        );
        const { defeated_count, total, earned_globetrotter, bonus } = res.data;
        if (earned_globetrotter) {
          toast.success(`🏆 GOLDEN GLOBETROTTER UNLOCKED! +${bonus} credits`, { duration: 8000 });
        } else if (res.data.newly_added) {
          toast(`Boss ${defeated_count}/${total} defeated`, { duration: 2500, icon: "🏆" });
        }
        refresh?.();
      } catch (e) {
        // Boss defeat tracking is best-effort; the riddle UX shouldn't fail
        // even if the network is flaky.
        console.error("boss defeat record failed", e);
      }
    } else {
      toast.error("Wrong! Bounced back.");
      setRiddle((cur) => ({ ...cur, open: false }));
    }
  }, [city, refresh]);

  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    sfx.setMuted(m);
  };
  const toggleView = () => setViewMode((v) => (v === "map" ? "cockpit" : "map"));

  const remaining = Math.max(0, 380 - elapsed);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(Math.floor(remaining % 60)).padStart(2, "0");

  if (!city || !car) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-white">
        <div className="label-kicker animate-pulse">Loading race…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Top HUD */}
      <div className="sticky top-0 z-30 border-b border-white/10 bg-black/90 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 lg:px-6 py-3">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/lobbies")} className="text-white/60 hover:text-white" data-testid="race-exit">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="label-kicker">Race · {city.name}</div>
              <div className="font-heading text-lg leading-none">{city.tagline}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleFullscreen}
              data-testid="race-fullscreen-toggle"
              className="flex items-center gap-1 border border-white/15 hover:border-[#FFCC00] hover:text-[#FFCC00] px-3 py-2 transition-colors duration-150"
              title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen (F)"}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              <span className="text-xs font-mono tracking-widest uppercase">
                {isFullscreen ? "Exit" : "Full"}
              </span>
            </button>
            <button
              onClick={toggleView}
              data-testid="race-view-toggle"
              className="flex items-center gap-1 border border-white/15 hover:border-[#FFCC00] hover:text-[#FFCC00] px-3 py-2 transition-colors duration-150"
              title="Toggle view (V)"
            >
              {viewMode === "map" ? <CarIcon className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span className="text-xs font-mono tracking-widest uppercase">{viewMode === "map" ? "Cockpit" : "Map"}</span>
            </button>
            <button
              onClick={toggleMute}
              data-testid="race-mute-toggle"
              className="text-white/60 hover:text-white"
              title={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <Hud label="Timer" value={`${mm}:${ss}`} accent="#FFCC00" testid="hud-timer" warning={remaining < 60} />
            <Hud label="Items" value={`${collected}/${city.items.length}`} accent="#34C759" testid="hud-items" />
            <Hud label="HP" value={`${Math.max(0, Math.floor(hp))}`} accent="#FF3B30" testid="hud-hp" />
            <Hud label="Coins" value={currency.toLocaleString()} accent="#FFCC00" testid="hud-currency" />
          </div>
        </div>
        <div className="px-4 lg:px-6 py-2 flex items-center gap-2 border-t border-white/5 overflow-x-auto">
          <WeaponBtn icon={Target} label="Missile" count={weapons.missile} hint="SPACE" testid="hud-missile" />
          <WeaponBtn icon={Zap} label="Boost" count={weapons.rocket_boost} hint="SHIFT" testid="hud-boost" />
          <WeaponBtn icon={Bot} label="Monkey" count={weapons.monkey_bomb} hint="E" testid="hud-monkey" />
          {/* ULTIMATE — once-per-race chassis special. Pulses gold when ready. */}
          <div
            className={`flex items-center gap-2 px-3 py-1.5 border-2 transition-all ${
              ultimateState.ready
                ? "border-[#FFCC00] bg-[#FFCC00]/15 text-[#FFCC00] anim-pulse-red"
                : ultimateState.used
                  ? "border-white/10 text-white/30"
                  : "border-white/10 text-white/40"
            }`}
            data-testid="hud-ultimate"
            title={
              ultimateState.ready
                ? "Press U or Enter to fire your ultimate"
                : ultimateState.used
                  ? "Already used this race"
                  : "Arms 2 s after race start"
            }
          >
            <Zap className="w-4 h-4" />
            <div className="leading-tight">
              <div className="font-heading text-[10px] uppercase tracking-[0.18em]">
                {ultimateState.label || "Ultimate"}
              </div>
              <div className="text-[9px] tracking-[0.2em] uppercase font-mono">
                {ultimateState.used ? "USED" : ultimateState.ready ? "READY · U/⏎" : "CHARGING"}
              </div>
            </div>
          </div>
          <div className="flex-1" />
          <div className="font-mono text-[10px] text-white/40 tracking-widest uppercase hidden md:block">
            WASD · SPACE fire · SHIFT boost · E monkey · U/⏎ ultimate · F fullscreen
          </div>
        </div>
      </div>

      <div className={isFullscreen ? "p-0" : "p-2 lg:p-4"}>
        <div
          // In fullscreen we drop the 1280px cap and let the viewport fill
          // the screen while preserving the 1280:820 aspect ratio. We
          // reserve ~160px for the stacked top HUDs so the canvas never
          // overflows below the viewport (which was the "car drove off
          // the bottom of the screen" bug).
          className={
            isFullscreen
              ? "relative mx-auto w-full max-w-[calc((100vh-160px)*1280/820)] max-h-[calc(100vh-160px)] border border-white/10 bg-black"
              : "relative mx-auto w-full max-w-[1280px] border border-white/10 bg-black"
          }
          style={{ aspectRatio: "1280 / 820" }}
          data-testid="race-viewport"
        >
          <GameCanvas
            city={city}
            car={car}
            localUserId={user?.user_id}
            onItemTouched={onItemTouched}
            onFinish={onFinish}
            onHpChange={setHp}
            onWeaponsChange={setWeapons}
            onItemsChange={setCollected}
            onPedestrianHit={onPedestrianHit}
            onMonkeyDeploy={() => setMonkeyCinematicAt(performance.now())}
            onTourismProgress={onTourismProgress}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            remotePlayers={remote}
            sendEvent={sendEvent}
            registerRemoteHandlers={handleRegisterRemoteHandlers}
            registerPickupResolver={handleRegisterPickupResolver}
            registerCanvasActions={registerCanvasActions}
            onJailChange={handleJailChange}
            onGangChange={({ state }) => {
              if (state === "chasing") {
                toast("🚐 Carjackers spotted — keep moving!", { icon: "⚠️", duration: 3000 });
              } else if (state === "stolen") {
                toast.error("🚨 CARJACKED! Mash E to escape!", { duration: 4000 });
              } else if (state === "fleeing") {
                toast.success("💪 You shook them off!", { duration: 3000 });
              }
            }}
            onRouteEdgeCross={handleRouteEdgeCross}
            onBossEncounter={handleBossEncounter}
            sidekickDesign={sidekickDesign}
            catchphrase={catchphrase}
            onUltimateChange={setUltimateState}
            copStealStatus={copStealStatus}
            onCopStealTrigger={onCopStealTrigger}
            onOilSlick={() => {
              toast("🛢️ OIL SLICK — cops spinning out!", {
                icon: "🛢️",
                duration: 2500,
              });
            }}
            onUltimateRejected={(reason) => {
              // Fired when the player hits U/Enter but the ultimate
              // can't fire. Surfacing the reason removes the "is the
              // key even bound?" confusion the silent failure caused.
              if (reason === "used") {
                toast("Ultimate already used this race", { icon: "⚡", duration: 2000 });
              } else {
                toast("Ultimate arms 2s into the race", { icon: "⏱️", duration: 2000 });
              }
            }}
            paused={quiz.open || !!raceEnded}
          />
          <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_80px_rgba(0,0,0,0.9)]" />

          {/* Trunk-Monkey cinematic overlay */}
          <MonkeyCinematic startedAt={monkeyCinematicAt} onDone={() => setMonkeyCinematicAt(0)} />

          {/* floating -50 penalty indicators */}
          <div className="pointer-events-none absolute right-6 top-1/3 space-y-1" data-testid="coin-penalty-feed">
            {coinFlashes.map((f) => (
              <div
                key={f.id}
                className="font-heading text-3xl text-[#FF3B30] animate-pulse flex items-center gap-1"
                style={{ textShadow: "2px 2px 0 #000" }}
              >
                <Coins className="w-5 h-5" />
                -{f.amount}
              </div>
            ))}
          </div>

          {/* pedestrians-hit badge */}
          {pedestriansHit > 0 && (
            <div
              data-testid="pedestrians-hit-badge"
              className="absolute left-3 top-3 bg-[#E63946]/90 border-2 border-black text-white px-3 py-1 font-heading text-sm"
            >
              💥 {pedestriansHit} hit
            </div>
          )}

          {/* Tourism Bonus HUD: shows live "Tourist X/Y" progress while
              the player is exploring the city's signature props. Hidden if
              the city has no props (e.g., during dev / new cities). */}
          {tourism.total > 0 && (
            <div
              data-testid="tourism-progress-hud"
              className={`absolute right-3 bottom-3 px-3 py-2 border-2 font-heading uppercase tracking-wider text-xs flex items-center gap-2 ${
                tourism.visited >= tourism.total
                  ? "bg-[#FFCC00] text-black border-black animate-pulse"
                  : "bg-black/75 text-[#FFCC00] border-[#FFCC00]/40"
              }`}
            >
              <Trophy className="w-4 h-4" />
              <span>
                Tourist {tourism.visited}/{tourism.total}
                {tourism.visited >= tourism.total && " · LEGEND READY"}
              </span>
            </div>
          )}

          {/* In-jail "Sell Artifact" escape button. Renders only while the
              player is jailed AND has at least one collected artifact. Clicking
              it forfeits the most recent pickup and instantly ends jail. */}
          {jail.active && collected > 0 && (
            <button
              data-testid="jail-sell-artifact-btn"
              onClick={() => {
                const ok = canvasActionsRef.current?.sellItemToEscape?.();
                if (ok) {
                  toast("Artifact sold — you're free!", { icon: "🪙" });
                  // Canvas calls onItemsChange→setCollected so we don't need
                  // to decrement here. Just close the jail HUD locally; the
                  // canvas will fire onJailChange({active:false}) next frame.
                  setJail({ active: false });
                } else {
                  toast.error("Nothing to sell.");
                }
              }}
              className="absolute left-1/2 -translate-x-1/2 bottom-16 z-50 px-6 py-3 bg-[#34C759] text-black font-heading uppercase text-sm tracking-wider border-4 border-black hover:bg-[#22A04E] transition-colors animate-pulse"
            >
              💰 Sell Artifact (-1) → Escape Jail
            </button>
          )}

          {feed.length > 0 && (
            <div className="absolute left-3 bottom-3 space-y-1" data-testid="race-activity-feed">
              {feed.map((f) => (
                <div key={f.id} className="font-mono text-[10px] tracking-widest uppercase bg-black/70 border border-white/10 px-2 py-1 text-white/70">
                  {f.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <QuizModal
        open={quiz.open}
        cityId={city.id}
        item={quiz.item}
        quizData={quiz.item ? quizCache[quiz.item.id] : null}
        onAnswer={onQuizAnswer}
        onClose={onQuizClose}
      />

      <RiddleModal
        open={riddle.open}
        riddle={riddle.question ? { question: riddle.question, options: riddle.options } : null}
        bossName={riddle.bossName}
        onAnswer={handleRiddleAnswer}
        onClose={() => setRiddle((r) => ({ ...r, open: false }))}
      />

      {raceEnded && <RaceSummary result={raceEnded} onExit={() => navigate("/leaderboard")} />}

      {bossFight && (
        <BossFightModal
          cityId={bossFight.city_id}
          driver={bossDriver}
          onClose={() => setBossFight(null)}
          onVictory={(rewards) => {
            setBossDefeated(true);
            // Show a celebratory toast that lasts long enough to read
            toast.success(
              `🥊 KO'd ${rewards.badge}! +${rewards.currency.toLocaleString()}¢` +
              (rewards.world_champion_earned ? " · WORLD CHAMPION 🏆" : ""),
              { duration: 6000 },
            );
            refresh?.();
          }}
        />
      )}
    </div>
  );
}

function Hud({ label, value, accent, testid, warning }) {
  return (
    <div className="text-right" data-testid={testid}>
      <div className="label-kicker">{label}</div>
      <div
        className={`font-pixel text-base lg:text-lg ${warning ? "text-[#FF3B30] animate-pulse" : ""}`}
        style={!warning ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

function WeaponBtn({ icon: Icon, label, count, hint, testid }) {
  return (
    <div className="flex items-center gap-2 border border-white/10 px-3 py-2" data-testid={testid}>
      <Icon className="w-4 h-4 text-[#FF3B30]" />
      <div>
        <div className="label-kicker leading-none">{label}</div>
        <div className="font-heading text-sm">
          ×{count} <span className="font-mono text-[10px] text-white/40">[{hint}]</span>
        </div>
      </div>
    </div>
  );
}

function RaceSummary({ result, onExit }) {
  const mm = String(Math.floor(result.time_seconds / 60)).padStart(2, "0");
  const ss = String(Math.floor(result.time_seconds % 60)).padStart(2, "0");
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-6" data-testid="race-summary">
      <div className="surface-solid max-w-md w-full p-8 text-center relative">
        <div className="label-kicker mb-2">Race complete</div>
        <div className="font-heading text-5xl">{result.won ? "You won" : result.finished ? "Finished" : "Time up"}</div>
        {result.won ? (
          <Trophy className="w-14 h-14 mx-auto mt-6 text-[#FFCC00]" />
        ) : (
          <Flag className="w-14 h-14 mx-auto mt-6 text-[#FF3B30]" />
        )}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="border border-white/10 p-3">
            <div className="label-kicker">Items</div>
            <div className="font-heading text-2xl">{result.items_collected}</div>
          </div>
          <div className="border border-white/10 p-3">
            <div className="label-kicker">Time</div>
            <div className="font-heading text-2xl font-mono">{mm}:{ss}</div>
          </div>
          {result.reward !== undefined && (
            <div className="border border-white/10 p-3">
              <div className="label-kicker">Reward</div>
              <div className="font-heading text-2xl text-[#FFCC00]">+{result.reward}</div>
            </div>
          )}
          {result.pedestrians_hit > 0 && (
            <div className="border border-[#FF3B30]/40 p-3">
              <div className="label-kicker text-[#FF3B30]">Ped. hit</div>
              <div className="font-heading text-2xl text-[#FF3B30]">{result.pedestrians_hit}</div>
              <div className="text-xs text-white/50 font-mono mt-1">-{result.penalty} live</div>
            </div>
          )}
          {result.tourism_target > 0 && (
            <div
              className={`border p-3 col-span-2 ${
                result.local_legend ? "border-[#FFCC00] bg-[#FFCC00]/10" : "border-white/10"
              }`}
              data-testid="race-summary-tourism"
            >
              <div className="label-kicker">{result.local_legend ? "🏆 Local Legend Earned" : "Tourist"}</div>
              <div className="font-heading text-2xl text-[#FFCC00]">
                {result.tourism_visited}/{result.tourism_target}
              </div>
              {result.local_legend && (
                <div className="mt-1 text-xs uppercase tracking-widest text-[#FFCC00]/90 font-heading">
                  {result.badge} · +{result.tourism_bonus} bonus
                </div>
              )}
              {!result.local_legend && result.tourism_target > result.tourism_visited && (
                <div className="mt-1 text-[10px] text-white/50">
                  Visit all {result.tourism_target} props + collect every artifact for the legend bonus.
                </div>
              )}
            </div>
          )}
        </div>
        <button onClick={onExit} className="btn-primary mt-8 w-full" data-testid="race-summary-exit">
          View Rankings
        </button>
      </div>
    </div>
  );
}


// ============================================================================
// MonkeyCinematic — full-screen zoom-in overlay triggered when the player
// deploys the Trunk Monkey. Uses the user-provided monkey sprite, animates
// a dramatic ZOOM + SHAKE + "DEPLOYED" banner, then fades out after ~1500ms.
// ============================================================================
function MonkeyCinematic({ startedAt, onDone }) {
  const [t, setT] = useState(0); // elapsed ms since startedAt (0..DURATION)
  const DURATION = 1600;

  useEffect(() => {
    if (!startedAt) { setT(0); return; }
    let raf;
    const step = () => {
      const elapsed = performance.now() - startedAt;
      setT(elapsed);
      if (elapsed >= DURATION) {
        onDone?.();
      } else {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [startedAt, onDone]);

  if (!startedAt || t >= DURATION) return null;

  // Three-phase animation: ZOOM (0-500ms), HOLD (500-1200ms), FADE (1200-1600ms)
  const progress = t / DURATION;
  let scale, opacity, shake;
  if (t < 500) {
    scale = 0.2 + (t / 500) * 0.8;
    opacity = t / 500;
    shake = 0;
  } else if (t < 1200) {
    scale = 1 + Math.sin(t / 40) * 0.03;
    opacity = 1;
    shake = 6 * Math.sin(t / 30);
  } else {
    const fade = (t - 1200) / 400;
    scale = 1 - fade * 0.15;
    opacity = 1 - fade;
    shake = 0;
  }

  // FRAME CYCLING: show the "hero" pose during the zoom-in, then flip through
  // the 3 attack frames (punch → spin-tail → jump-smash) during the hold phase.
  // This is the "destruction" the player asked for — the monkey actively
  // brawls on-screen instead of standing still.
  const frames = [
    { src: "monkey_hero.png",          label: "TRUNK MONKEY!" },
    { src: "monkey_attack1_punch.png", label: "POW! PUNCH!" },
    { src: "monkey_attack2_spin.png",  label: "TAIL SPIN!" },
    { src: "monkey_attack3_smash.png", label: "JUMP SMASH!" },
  ];
  let frameIdx;
  if (t < 500) frameIdx = 0;              // hero during zoom
  else if (t < 730)  frameIdx = 1;        // punch
  else if (t < 960)  frameIdx = 2;        // spin tail
  else if (t < 1200) frameIdx = 3;        // jump smash
  else frameIdx = 0;                       // back to hero during fade
  const frame = frames[frameIdx];

  return (
    <div
      data-testid="monkey-cinematic"
      className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden z-50"
      style={{ opacity }}
    >
      {/* Dark vignette flash */}
      <div className="absolute inset-0 bg-black" style={{ opacity: 0.55 * opacity }} />

      {/* Radial yellow explosion glow behind the monkey */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 50%, rgba(255,204,0,${0.55 * opacity}) 0%, rgba(255,120,30,${0.35 * opacity}) 20%, rgba(230,57,70,${0.2 * opacity}) 40%, rgba(0,0,0,0) 65%)`,
          transform: `scale(${1 + progress * 0.3})`,
        }}
      />

      {/* Comic-style radial lines — only far from center, so monkey stays clear */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
        {Array.from({ length: 14 }).map((_, i) => {
          const angle = (i / 14) * Math.PI * 2 + progress * 0.8;
          const x1 = 50 + Math.cos(angle) * 50;
          const y1 = 50 + Math.sin(angle) * 50;
          const xStart = 50 + Math.cos(angle) * 26;
          const yStart = 50 + Math.sin(angle) * 26;
          return (
            <line
              key={i}
              x1={xStart}
              y1={yStart}
              x2={x1}
              y2={y1}
              stroke="#FFCC00"
              strokeWidth="0.6"
              opacity={opacity * 0.7}
            />
          );
        })}
      </svg>

      {/* The monkey sprite — BIG, zoomed, shaky, pixelated. THE HERO. */}
      <img
        src={`${BACKEND_URL}/api/static/sprites/${frame.src}?v=2`}
        alt="Trunk Monkey"
        className="relative z-10"
        style={{
          width: "55%",
          maxWidth: 560,
          imageRendering: "pixelated",
          transform: `scale(${scale * 1.3}) translate(${shake}px, ${Math.sin(t / 25) * shake * 0.6}px)`,
          filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.9)) drop-shadow(0 0 40px rgba(255,204,0,0.8))",
        }}
      />

      {/* Dramatic headline — changes text with each attack frame */}
      <div
        className="absolute top-1/4 w-full text-center"
        style={{
          transform: `scale(${scale * 1.1}) rotate(${-3 + Math.sin(t / 80) * 2}deg)`,
          opacity,
        }}
      >
        <div
          className="font-pixel text-[#FFCC00] inline-block px-4 py-2"
          style={{
            fontSize: "clamp(28px, 5vw, 58px)",
            textShadow: "4px 4px 0 #E63946, 8px 8px 0 #000",
            letterSpacing: "2px",
          }}
        >
          {frame.label}
        </div>
      </div>

      {/* Bottom tagline */}
      <div
        className="absolute bottom-[18%] w-full text-center font-pixel text-white"
        style={{ fontSize: "clamp(10px, 1.6vw, 18px)", textShadow: "2px 2px 0 #000", opacity }}
      >
        DEPLOYED · PREPARE FOR DESTRUCTION
      </div>

      {/* Floating pixel debris / banana peels */}
      {Array.from({ length: 12 }).map((_, i) => {
        const ang = (i / 12) * Math.PI * 2;
        const d = 120 + progress * 280;
        return (
          <div
            key={i}
            className="absolute"
            style={{
              left: "50%",
              top: "50%",
              width: 10,
              height: 10,
              background: i % 3 === 0 ? "#FFCC00" : i % 3 === 1 ? "#E63946" : "#FFF",
              transform: `translate(${Math.cos(ang) * d}px, ${Math.sin(ang) * d}px) rotate(${progress * 720}deg)`,
              opacity: opacity * (1 - progress * 0.6),
              imageRendering: "pixelated",
            }}
          />
        );
      })}
    </div>
  );
}
